import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { paymentAPI, orderAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Shield, Lock } from 'lucide-react';

const inputStyle = { width: '100%', padding: '12px 16px', background: 'var(--dark)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '13px', marginBottom: '12px', outline: 'none' };

const CheckoutPage = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { cart, totalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1=shipping, 2=payment

  const [shipping, setShipping] = useState({
    fullName: user?.name || '',
    street: user?.address?.street || '',
    city: user?.address?.city || '',
    state: user?.address?.state || '',
    zipCode: user?.address?.zipCode || '',
    country: user?.address?.country || '',
    phone: user?.phone || '',
  });

  const shippingCost = totalPrice >= 200 ? 0 : 15;
  const taxPrice = parseFloat((totalPrice * 0.08).toFixed(2));
  const orderTotal = parseFloat((totalPrice + shippingCost + taxPrice).toFixed(2));

  const handleShippingChange = e => setShipping(s => ({ ...s, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const required = ['fullName', 'street', 'city', 'state', 'zipCode', 'country', 'phone'];
    for (const field of required) {
      if (!shipping[field]) { toast.error(`Please fill in ${field}`); return; }
    }

    setProcessing(true);
    try {
      // Create order first
      const { data: order } = await orderAPI.create({ shippingAddress: shipping, paymentMethod: 'stripe' });

      // Create payment intent
      const { data: intentData } = await paymentAPI.createIntent({ amount: orderTotal });

      // Confirm card payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(intentData.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { name: shipping.fullName, email: user.email },
        },
      });

      if (error) {
        toast.error(error.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        await paymentAPI.confirm({ paymentIntentId: paymentIntent.id, orderId: order._id });
        await clearCart();
        toast.success('Order placed successfully!');
        navigate(`/order-success/${order._id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Checkout failed');
      setProcessing(false);
    }
  };

  return (
    <div style={{ paddingTop: '72px', minHeight: '100vh' }}>
      <div style={{ background: 'var(--dark)', padding: '40px 0', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <p style={{ fontSize: '10px', letterSpacing: '0.3em', color: 'var(--gold)', marginBottom: '8px' }}>SECURE CHECKOUT</p>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '40px', fontWeight: 400 }}>Checkout</h1>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '40px', paddingBottom: '80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '40px', alignItems: 'start' }}>
          <form onSubmit={handleSubmit}>
            {/* Shipping */}
            <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '32px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: 400 }}>Shipping Details</h2>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--gold)', color: 'var(--black)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700 }}>1</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                {[
                  { name: 'fullName', placeholder: 'Full Name', span: 2 },
                  { name: 'street', placeholder: 'Street Address', span: 2 },
                  { name: 'city', placeholder: 'City' },
                  { name: 'state', placeholder: 'State / Province' },
                  { name: 'zipCode', placeholder: 'ZIP / Postal Code' },
                  { name: 'country', placeholder: 'Country' },
                  { name: 'phone', placeholder: 'Phone Number', span: 2 },
                ].map(({ name, placeholder, span }) => (
                  <input key={name} name={name} placeholder={placeholder} value={shipping[name]} onChange={handleShippingChange}
                    required style={{ ...inputStyle, gridColumn: span === 2 ? 'span 2' : 'auto' }} />
                ))}
              </div>
            </div>

            {/* Payment */}
            <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: 400 }}>Payment</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Lock size={14} color="var(--gold)" />
                  <span style={{ fontSize: '11px', color: 'var(--gold)', letterSpacing: '0.1em' }}>SECURE</span>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--gold)', color: 'var(--black)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700 }}>2</div>
                </div>
              </div>

              <div style={{ background: 'var(--dark)', border: '1px solid var(--border)', padding: '16px', marginBottom: '24px' }}>
                <CardElement options={{
                  style: {
                    base: { fontSize: '14px', color: '#f5f0e8', fontFamily: 'Montserrat, sans-serif', '::placeholder': { color: 'rgba(245,240,232,0.4)' } },
                    invalid: { color: '#ef4444' },
                  },
                }} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', fontSize: '11px', color: 'var(--white-dim)' }}>
                <Shield size={14} color="var(--gold)" /> Powered by Stripe. Your payment info is encrypted.
              </div>

              <button type="submit" disabled={!stripe || processing}
                style={{ width: '100%', background: processing ? 'var(--gold-dark)' : 'var(--gold)', color: 'var(--black)', padding: '14px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', cursor: processing ? 'not-allowed' : 'pointer', transition: 'background 0.3s' }}>
                {processing ? 'PROCESSING...' : `PLACE ORDER — $${orderTotal}`}
              </button>
            </div>
          </form>

          {/* Summary */}
          <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '32px', position: 'sticky', top: '88px' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 400, marginBottom: '20px' }}>Order Summary</h3>
            <div style={{ maxHeight: '280px', overflowY: 'auto', marginBottom: '20px' }}>
              {cart.items?.map(item => (
                <div key={item._id} style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
                  <div style={{ width: '48px', height: '48px', background: 'var(--dark-3)', flexShrink: 0, overflow: 'hidden' }}>
                    {item.product?.images?.[0] && <img src={item.product.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '12px', marginBottom: '2px' }}>{item.name}</p>
                    <p style={{ fontSize: '11px', color: 'var(--white-dim)' }}>Qty: {item.quantity}</p>
                  </div>
                  <span style={{ fontSize: '13px' }}>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            {[['Subtotal', `$${totalPrice.toFixed(2)}`], ['Shipping', shippingCost === 0 ? 'Free' : `$${shippingCost}`], ['Tax', `$${taxPrice}`]].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--white-dim)', marginBottom: '10px' }}>
                <span>{l}</span><span style={{ color: 'var(--white)' }}>{v}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-display)', fontSize: '22px', marginTop: '12px' }}>
              <span>Total</span><span style={{ color: 'var(--gold)' }}>${orderTotal}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
